import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FiGithub, FiExternalLink, FiCode, FiPenTool, FiMonitor, FiX, FiStar, FiMail } from 'react-icons/fi';
import { FaLinkedinIn } from 'react-icons/fa';
import ScrollGrid from './ScrollGrid';
import FlickerText from './FlickerText';
import Lenis from 'lenis';
import { useLenis } from '../LenisContext';
import './Projects.css';

const categories = [
  { label: 'Front-end Development', icon: <FiCode /> },
  { label: 'UI/UX Design', icon: <FiPenTool /> },
  { label: 'Full-stack Development', icon: <FiMonitor /> },
];

const projectsData = [
  {
    title: 'InternTrack – Homepage',
    description: 'The public-facing homepage of the InternTrack system, introducing the platform and its features to students and institutions.',
    image: '/InternTrack-Homepage.png',
    roles: ['Front-end Developer'],
    special: false,
    link: 'https://jay-marc-merilo-portfolio1.netlify.app/portfolio/',
  },
  {
    title: 'InternTrack – Admin',
    description: 'The admin dashboard of InternTrack for managing interns, tracking submissions, and overseeing institutional progress.',
    image: '/Admin-InternTrack.png',
    roles: ['Front-end Developer'],
    special: false,
    link: 'https://jay-marc-merilo-portfolio1.netlify.app/portfolio/',
  },

  {
    title: 'AutoSort',
    description: 'A utility tool that automatically sorts and organizes data with a clean, efficient interface.',
    image: '/AutoSort.png',
    roles: ['Developer'],
    special: false,
    link: 'https://github.com/emarcj0-debug/Auto-SorterEXE',
  },
  {
    title: 'Portfolio',
    description: 'My personal portfolio website showcasing my projects, skills, and experience as a web developer and designer.',
    image: '/My-Portfolio.png',
    roles: ['Developer'],
    special: false,
    link: 'https://jay-marc-merilo-portfolio.netlify.app/',
  },
    {
    title: 'Doki Takoyaki',
    description: 'A E-commerce website for a fictional takoyaki brand, featuring a vibrant design and user-friendly interface to explore the menu and place orders.',
    image: '/Doki-Takoyaki.png',
    roles: ['Front-end Developer'],
    special: false,
    link: 'https://jay-marc-merilo-portfolio1.netlify.app/portfolio/',
  },
    {
    title: 'Coziest',
    description: 'A E-commerce website for a blanket brand, featuring a cozy design and user-friendly interface to explore the products and place orders.',
    image: '/Coziest.png',
    roles: ['Front-end Developer'],
    special: false,
    link: 'https://jay-marc-merilo-portfolio1.netlify.app/portfolio/',
  },
];

const Projects = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.15 });
  const [showGallery, setShowGallery] = useState(false);
  const mainLenis = useLenis();

  const galleryRef = useRef(null);

  // Lock body scroll when gallery is open & init Lenis smooth scroll for gallery
  useEffect(() => {
    if (!showGallery) {
      // Resume main page smooth scroll
      if (mainLenis) mainLenis.start();
      document.body.style.overflow = '';
      return;
    }

    // Stop main page smooth scroll so it doesn't conflict
    if (mainLenis) mainLenis.stop();
    document.body.style.overflow = 'hidden';

    const el = galleryRef.current;
    if (!el) return;

    const lenis = new Lenis({
      wrapper: el,
      content: el.firstElementChild,
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
      document.body.style.overflow = '';
    };
  }, [showGallery, mainLenis]);

  const handleViewAll = (e) => {
    e.preventDefault();
    setShowGallery(true);
  };

  return (
    <section className="projects" id="projects" ref={ref}>
      <ScrollGrid fadeVariant="fade-bottom-left" />

      {/* Full-screen Projects Gallery */}
      <AnimatePresence>
        {showGallery && (
          <motion.div
            className="gallery-overlay"
            ref={galleryRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Grid gradient background */}
            <div className="gallery-grid-bg" />

            <div className="gallery-content">
              {/* Close Button */}
              <button
                className="gallery-close clickable"
                onClick={() => setShowGallery(false)}
              >
                <FiX size={22} />
              </button>

              {/* Gallery Header — centered */}
              <div className="gallery-header">
                <h2 className="gallery-title">PROJECTS</h2>
                <p className="gallery-subtitle">
                  This section showcases my innovative web development and collaborative projects.
                  Each one reflects my passion for creating designs that are both visually appealing and functional.
                </p>
              </div>

              {/* Projects Grid */}
              <div className="gallery-grid">
                {projectsData.map((project, index) => (
                  <motion.a
                    className="gallery-card"
                    key={index}
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: index * 0.1 }}
                  >
                    <div className="gallery-card-image-wrapper">
                      <div className="gallery-card-image">
                        <img src={project.image} alt={project.title} />
                      </div>
                    </div>
                    <div className="gallery-card-body">
                      <div className="gallery-card-title-row">
                        <h3>{project.title}</h3>
                        {project.special && (
                          <span className="gallery-badge">
                            <FiStar size={12} />
                            Special project
                          </span>
                        )}
                      </div>
                      <p className="gallery-card-desc">{project.description}</p>
                      <div className="gallery-card-roles">
                        <span className="gallery-roles-label">Role/s:</span>
                        <div className="gallery-role-tags">
                          {project.roles.map((role, i) => (
                            <span key={i} className="gallery-role-tag">{role}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>

              {/* Gallery Footer */}
              <div className="gallery-footer">
                <div className="gallery-footer-divider" />
                <div className="gallery-footer-main">
                  <div className="gallery-footer-brand">
                    <h4>Jay Marc C. Merilo</h4>
                    <p>UI/UX Designer &amp; Developer</p>
                  </div>
                  <div className="gallery-footer-socials">
                    <a href="https://github.com/emarcj0-debug" target="_blank" rel="noopener noreferrer" className="gallery-footer-social clickable" aria-label="GitHub">
                      <FiGithub />
                    </a>
                    <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer" className="gallery-footer-social clickable" aria-label="LinkedIn">
                      <FaLinkedinIn />
                    </a>
                    <a href="mailto:jaymarccmerilo@gmail.com" className="gallery-footer-social clickable" aria-label="Email">
                      <FiMail />
                    </a>
                  </div>
                </div>
                <p className="gallery-footer-copyright">© 2026 Jay Marc. All rights reserved.</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container">
        <div className="projects-split">
          {/* Left Side - Title & Info */}
          <motion.div
            className="projects-left"
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0.3, x: -20 }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="projects-massive-title">
              <FlickerText text="PROJECTS" isInView={isInView} />
            </h2>

            <div className="projects-categories">
              {categories.map((cat, i) => (
                <motion.div
                  className="project-category-tag"
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0.3, y: 15 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                >
                  {cat.icon}
                  <span>{cat.label}</span>
                </motion.div>
              ))}
            </div>

            <p className="projects-intro">
              This section highlights my creative web development and collaboration
              projects. In these, I take on multiple roles: Front-end Developer, UI/UX
              and Graphic Designer to deliver high-quality websites and web applications.
            </p>

            <div className="projects-actions">
              <button
                onClick={handleViewAll}
                className="btn-primary clickable"
              >
                View all
                <FiExternalLink />
              </button>
              <a
                href="https://github.com/emarcj0-debug"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline clickable"
              >
                View on GitHub
                <FiGithub />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
